import axios from "axios";
import _, { debounce } from "lodash";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";

import EditMode, { EditModeRef, Data, Point } from "components/EditMode";
import Guide from "components/Guide";
import ShotContext from "components/Shot/ShotContext";
import TrackingGuide from "components/TrackingGuide";
import { DEMO_URLS, UserGuides, SERVICES } from "constants/constants";
import {
  createCutout,
  getCutouts,
  getEditableCutout,
  updateCutout,
} from "services/CutoutService";
import { createLayer } from "services/LayerService";

import styles from "./Workspace.module.scss";
import { CutoutInProgress } from "../../models/Cutout";
import { PredictionResponse } from "../../models/Prediction";
import { postAPIService } from "../../services/BaseServce";
import {
  ACTION_SUBSCRIBE_ANNOTATION_UPDATE,
  getRequestData,
  getSocket,
  closeSocket,
} from "../../services/WebSocketService";
import { handleImageScale } from "../../utils/sam";

function dataURLToBlob(dataURL: string): Blob {
  const [header, base64] = dataURL.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "application/octet-stream";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

function isMaskAllBlack(blob: Blob): Promise<boolean> {
  return createImageBitmap(blob).then((bitmap) => {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return false;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 0; i < data.length; i += 4) {
      if (data[i] !== 0 || data[i + 1] !== 0 || data[i + 2] !== 0) {
        return false;
      }
    }
    canvas.width = 0;
    canvas.height = 0;
    return true;
  });
}

interface WorkspaceParams {
  onBack: () => void;
  color: string;
}

type SavePayload = {
  maskImageData: string;
  coverImageData: string;
  previewCompositeData?: string;
};

const Workspace: React.FC<WorkspaceParams> = ({ onBack, color }) => {
  const {
    imageData,
    layers,
    selectedFrame: frame,
    shotId,
    selectedLayer,
    setSelectedLayer,
    setLayers,
    setShotCutouts,
    setCutoutInProgress,
    cutoutInProgress,
    service,
    selectedFrame,
    isAnnotationSaving,
    setIsAnnotationSaving,
  } = useContext(ShotContext);
  const [data, setData] = useState<Data | null>(null);
  const [points, setPoints] = useState<Point[]>([]);
  const [clicksToProcess, setClicksToProcess] = useState<Point[] | null>(null);
  const [hoverToProcess, setHoverToProcess] = useState<Point[] | null>(null);
  const [processing, setProcessing] = useState<boolean>(true);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isLoadingMask, setIsLoadingMask] = useState(false);
  const [annotationId, setAnnotationId] = useState<number>();
  const [openSocket, setOpenSocket] = useState(false);
  const [baseMask, setBaseMask] = useState<ImageData | null>(null);
  const [analysisCompleted, setAnalysisCompleted] = useState(false);
  const [isLoadingPreviousMask, setIsLoadingPreviousMask] = useState(false);
  const [isCreatingCutout, setIsCreatingCutout] = useState(false);
  const [toSave, setToSave] = useState<SavePayload | null>(null);

  const editModeRef = useRef<EditModeRef>(null);
  const samWorkerRef = useRef<Worker | null>(null);

  const baseMaskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const maskImageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (service !== SERVICES.ROTOSCOPING) {
      // Cleanup existing SAM worker if service changed
      if (samWorkerRef.current) {
        samWorkerRef.current.terminate();
        samWorkerRef.current = null;
      }
      return;
    }

    if (!samWorkerRef.current) {
      const instance = new Worker(
        new URL("../../samWorker.ts", import.meta.url),
        { type: "module" },
      );
      samWorkerRef.current = instance;
    }
  }, [service]);

  const processEmbedding = useCallback((res: PredictionResponse) => {
    if (res.data.image_embeddings_url && samWorkerRef.current) {
      samWorkerRef.current.postMessage({
        type: "processEmbedding",
        data: { res: JSON.stringify(res) },
      });
    } else if (res.data.data.error) {
      setAnnotationId(undefined);
      setProcessing(false);
    }
  }, []);

  // Stopping polling when embedding is generated...
  useEffect(() => {
    if (!data || !shotId || !frame || service !== SERVICES.ROTOSCOPING) {
      setAnnotationId(undefined);
      return;
    }
    const fromData = new FormData();
    fromData.append("image", new File([data.file], "image.png"));
    fromData.append("shot_id", shotId.toString());
    fromData.append("frame", frame.toString());
    postAPIService("/annotations", fromData).then((res: PredictionResponse) => {
      setAnnotationId(res.data.id);
      if (res.data.image_embeddings_url) {
        processEmbedding(res);
      } else {
        setOpenSocket(true);
      }
    });
  }, [service, data, shotId, frame, processEmbedding]);

  useEffect(() => {
    if (annotationId && openSocket) {
      const socket = getSocket("annotation");
      if (!socket) {
        return () => {};
      }
      socket.onopen = (event) => {
        socket.send(
          getRequestData(ACTION_SUBSCRIBE_ANNOTATION_UPDATE, {
            id: annotationId,
          }),
        );
      };
      socket.onmessage = (event) => {
        const res = JSON.parse(event.data) as PredictionResponse;
        processEmbedding(res);
      };
      return () => {
        closeSocket("annotation");
        setOpenSocket(false);
      };
    }
    return () => {};
  }, [annotationId, openSocket, processEmbedding]);

  useEffect(() => {
    if (!imageData) return;
    fetch(imageData)
      .then((it) => it.blob())
      .then((blob) => {
        const file = new File([blob], "file.jpeg", {
          type: "image/jpeg",
          lastModified: new Date().getTime(),
        });
        if (!imageRef.current) {
          imageRef.current = new Image();
        }
        const img = imageRef.current;
        img.src = imageData;
        img.onload = () => {
          const { height, width, samScale } = handleImageScale(img);
          if (samWorkerRef.current)
            samWorkerRef.current.postMessage({
              type: "modelScale",
              data: {
                modelScale: {
                  height,
                  width,
                  samScale,
                },
              },
            });
          img.width = width;
          img.height = height;
          setData({
            width,
            height,
            file,
            img,
          });
        };
      });
  }, [imageData]);

  const sendPoints = useCallback(() => {
    if (isLoadingMask || !samWorkerRef.current) return;

    const pointsToSend: Point[] = [];
    let isHover = false;

    if (clicksToProcess) {
      pointsToSend.push(...clicksToProcess);
      setClicksToProcess(null);
    } else if (hoverToProcess) {
      pointsToSend.push(...points, ...hoverToProcess);
      setHoverToProcess(null);
      isHover = true;
    }

    samWorkerRef.current.postMessage({
      type: "process",
      data: { points: pointsToSend, isHover },
    });
  }, [clicksToProcess, hoverToProcess, isLoadingMask, points]);

  const getMask = useCallback((ps: Point[], isHover = false) => {
    if (isHover) setHoverToProcess(ps);
    else setClicksToProcess(ps);
  }, []);

  useEffect(() => {
    if ((clicksToProcess || hoverToProcess !== null) && !isLoadingMask) {
      sendPoints();
    }
  }, [clicksToProcess, hoverToProcess, isLoadingMask, sendPoints]);

  const uploadCutout = useRef(
    debounce(
      async (
        cutout: CutoutInProgress,
        maskImageData: string,
        coverImageData: string,
        previewCompositeData?: string,
      ) => {
        try {
          await updateCutout(cutout.id, true);

          const maskBlob = dataURLToBlob(maskImageData);
          const s3PutHeaders = { "Content-Type": "binary/octet-stream" };

          if (cutout.mask_img_presigned_url) {
            await axios.put(cutout.mask_img_presigned_url, maskBlob, {
              headers: s3PutHeaders,
            });
          }

          if (
            (service === SERVICES.ROTOSCOPING ||
              service === SERVICES.SMART_VECTORS) &&
            coverImageData &&
            cutout.cover_img_presigned_url
          ) {
            const coverBlob = dataURLToBlob(coverImageData);
            await axios.put(cutout.cover_img_presigned_url, coverBlob, {
              headers: s3PutHeaders,
            });
          }

          if (
            (service === SERVICES.ROTOSCOPING ||
              service === SERVICES.SMART_VECTORS) &&
            coverImageData &&
            cutout.layer_cover_image_presigned_url
          ) {
            const layerCoverBlob = dataURLToBlob(coverImageData);
            await axios.put(
              cutout.layer_cover_image_presigned_url,
              layerCoverBlob,
              {
                headers: s3PutHeaders,
              },
            );
          }

          if (
            previewCompositeData &&
            cutout.preview_img_presigned_url &&
            (service === SERVICES.TRACKING ||
              service === SERVICES.SMART_VECTORS)
          ) {
            const previewBlob = dataURLToBlob(previewCompositeData);
            await axios.put(cutout.preview_img_presigned_url, previewBlob, {
              headers: s3PutHeaders,
            });
          }

          const isBlank = await isMaskAllBlack(maskBlob);
          if (!isBlank) {
            await updateCutout(cutout.id, false);
          }

          setIsAnnotationSaving(false);
          if (
            (service === SERVICES.ROTOSCOPING ||
              service === SERVICES.SMART_VECTORS) &&
            shotId
          ) {
            try {
              const latestCutouts = await getCutouts(
                undefined,
                +shotId,
                service,
              );
              setShotCutouts(latestCutouts);
            } catch {
              // Ignore refresh failures after upload.
            }
          }
        } catch (error) {
          console.error(error);
          toast.error("Failed to upload cutout. Please try again.");
          setIsAnnotationSaving(false);
        }
      },
      500,
    ),
  ).current;

  const handleCreateCutout = useCallback(async () => {
    if (!shotId || !frame) return;
    const layer =
      selectedLayer ||
      (await createLayer(+shotId, service, selectedFrame).then(
        (result) => result,
      ));
    if (!layer) return;
    if (!selectedLayer) setSelectedLayer(layer);
    setLayers((oldLayers) =>
      [layer, ...(oldLayers?.filter((l) => l.id !== layer.id) || [])].sort(
        (l1, l2) => l2.id - l1.id,
      ),
    );
    if (!cutoutInProgress) {
      createCutout(frame, layer.id, annotationId).then((cutout) => {
        setCutoutInProgress(cutout);
        setIsCreatingCutout(false);
      });
    }
  }, [
    annotationId,
    cutoutInProgress,
    frame,
    selectedFrame,
    selectedLayer,
    service,
    setCutoutInProgress,
    setLayers,
    setSelectedLayer,
    shotId,
  ]);

  const handleSave = useCallback(
    async (
      maskImageData: string,
      coverImageData: string,
      previewCompositeData?: string,
    ) => {
      if (!selectedLayer || !cutoutInProgress || !frame) return;
      setIsAnnotationSaving(true);

      if (
        service === SERVICES.ROTOSCOPING ||
        service === SERVICES.SMART_VECTORS
      )
        selectedLayer.cover_image_url = coverImageData;
      if (service === SERVICES.TRACKING) selectedLayer.frame_number = frame;

      uploadCutout.cancel();
      uploadCutout(
        cutoutInProgress,
        maskImageData,
        coverImageData,
        previewCompositeData,
      );
      setToSave(null);
    },
    [
      cutoutInProgress,
      frame,
      selectedLayer,
      setIsAnnotationSaving,
      uploadCutout,
      service,
    ],
  );

  useEffect(() => {
    if (!toSave) return;
    if (!isCreatingCutout && !cutoutInProgress) {
      setIsCreatingCutout(true);
      handleCreateCutout();
    }
    if (selectedLayer && cutoutInProgress) {
      handleSave(
        toSave.maskImageData,
        toSave.coverImageData,
        toSave.previewCompositeData,
      );
    }
  }, [
    cutoutInProgress,
    handleCreateCutout,
    handleSave,
    isCreatingCutout,
    selectedLayer,
    toSave,
  ]);

  const generateBaseMask = useCallback(
    (maskImageURL: string, updateCanvas = false) => {
      if (!data) return;
      if (!baseMaskCanvasRef.current) {
        baseMaskCanvasRef.current = document.createElement("canvas");
      }
      const canvasMask = baseMaskCanvasRef.current;
      const { width, height } = data;
      canvasMask.width = width;
      canvasMask.height = height;
      const ctxMask = canvasMask.getContext("2d");
      if (!ctxMask) return;
      if (!maskImageRef.current) {
        maskImageRef.current = new Image();
      }
      const image = maskImageRef.current;
      image.crossOrigin = "Anonymous";
      image.onload = () => {
        ctxMask.drawImage(image, 0, 0, width, height);
        const maskData = ctxMask.getImageData(0, 0, width, height);
        setBaseMask(maskData);
        if (updateCanvas)
          editModeRef.current?.update(maskData.data, "rgb", false, true);
      };
      image.src = maskImageURL;
    },
    [data],
  );

  useEffect(() => {
    if (!cutoutInProgress && data) {
      setPoints([]);
      setBaseMask(null);
      const maskInput = Array.from(
        { length: data.width * data.height },
        () => 0,
      );
      editModeRef.current?.update(
        new Uint8ClampedArray(maskInput),
        "pixel",
        false,
        true,
      );
      if (service === SERVICES.ROTOSCOPING) {
        editModeRef.current?.selectMagicMode();
      }
    }
  }, [cutoutInProgress, data, service]);

  useEffect(() => {
    if (selectedLayer && frame && data) {
      setIsLoadingPreviousMask(true);
      getEditableCutout(selectedLayer.id, frame)
        .then((cutout) => {
          if (!cutout?.image_url) return;
          generateBaseMask(cutout.image_url, true);
          if (cutout.mask_img_presigned_url && cutout.cover_img_presigned_url)
            setCutoutInProgress(cutout);
        })
        .finally(() => setIsLoadingPreviousMask(false));
    }
  }, [data, frame, generateBaseMask, selectedLayer, setCutoutInProgress]);

  useEffect(() => {
    return () => {
      if (samWorkerRef.current) {
        samWorkerRef.current.terminate();
        samWorkerRef.current = null;
      }

      if (baseMaskCanvasRef.current) {
        baseMaskCanvasRef.current = null;
      }
      if (imageRef.current) {
        imageRef.current.onload = null;
        imageRef.current.src = "";
        imageRef.current = null;
      }

      if (maskImageRef.current) {
        maskImageRef.current.onload = null;
        maskImageRef.current.src = "";
        maskImageRef.current = null;
      }

      setBaseMask(null);

      uploadCutout.cancel();
    };
  }, [uploadCutout]);

  useEffect(() => {
    return () => {
      setCutoutInProgress(null);
      setSelectedLayer(null);
    };
  }, [setCutoutInProgress, setSelectedLayer]);

  useEffect(() => {
    if (!samWorkerRef.current) return;
    samWorkerRef.current.onmessage = (e) => {
      const { type, body } = e.data;
      switch (type) {
        case "maskImage":
          if (body?.maskImage) {
            editModeRef.current?.update(
              new Uint8ClampedArray(body.maskImage.data),
              "pixel",
              true,
            );
          } else if (data) {
            const maskInput = Array.from(
              { length: data.width * data.height },
              () => 0,
            );
            editModeRef.current?.update(
              new Uint8ClampedArray(maskInput),
              "pixel",
              false,
            );
          }
          break;
        case "model":
          setIsModelLoaded(body.isModelLoaded);
          break;
        case "processing":
          setProcessing(body.processing);
          break;
        case "loadingMask":
          setIsLoadingMask(body.loadingMask);
          break;
        // no default
      }
    };
  }, [data, service]);

  useEffect(() => {
    return () => {
      if (samWorkerRef.current) {
        samWorkerRef.current.postMessage({ type: "close" });
      }
    };
  }, []);

  useEffect(() => {
    if (!processing && isModelLoaded) {
      setAnalysisCompleted(true);
      setTimeout(() => {
        setAnalysisCompleted(false);
      }, 1000);
    }
  }, [processing, isModelLoaded]);

  useEffect(() => {
    return () => {
      baseMaskCanvasRef.current = null;
    };
  }, []);

  return (
    <div className={styles.container}>
      {data &&
        (processing || !isModelLoaded || analysisCompleted) &&
        samWorkerRef.current && (
          <div className={styles.hintContainer}>
            <div className={styles.hintContent}>
              <div className={styles.gifCon}>
                {analysisCompleted ? (
                  <img src="/done.gif" className={styles.doneGif} alt="" />
                ) : (
                  <img
                    src="/loading.gif"
                    className={styles.loadingGif}
                    alt=""
                  />
                )}
              </div>
              <div className={styles.hintText}>
                <p className={styles.hintHeader}>Analysing this frame...</p>
                <p className={styles.hintDesc}>
                  Shouldn't be too long. We should be ready to go in around 10
                  seconds
                </p>
              </div>
            </div>
          </div>
        )}
      <Guide
        id={UserGuides.ClickObject}
        open={
          !(processing || !isModelLoaded || analysisCompleted) &&
          service === SERVICES.ROTOSCOPING
        }
        placement="right"
        title="Click and select objects"
        description="You can now create annotations and select areas by clicking on different objects."
      >
        <div className={styles.guideTip} />
      </Guide>

      {data && imageData && (
        <EditMode
          ref={editModeRef}
          data={data}
          sourceImageData={imageData}
          isForReview={false}
          save={(maskImageData, coverImageData, previewCompositeData) => {
            setToSave({
              maskImageData,
              coverImageData,
              previewCompositeData,
            });
          }}
          color={color}
          done={onBack}
          onClear={() => {
            setPoints([]);
            setBaseMask(null);
          }}
          points={points}
          onHover={(ps) => {
            getMask(ps, true);
          }}
          setPoints={(ps) => {
            setPoints(ps);
            getMask(ps);
          }}
          isLoadingMask={isLoadingMask}
          cutoutInProgress={cutoutInProgress}
          generateBaseMask={generateBaseMask}
          baseMask={baseMask}
          isSaving={isAnnotationSaving}
          setIsSaving={setIsAnnotationSaving}
        />
      )}
      <TrackingGuide
        id={UserGuides.FirstKeyframe}
        open={!layers?.length && service === SERVICES.TRACKING}
        step={1}
        title="Select background"
        description="Select the background area to be tracked as shown on the left"
        secondaryTitle="Avoid selecting the following:"
        list={{
          "Moving objects": "Like a person walking, car driving, etc.",
          "Textureless elements": "Like the sky, an ocean etc.",
        }}
        videoUrl={DEMO_URLS.trackiongStepOne}
      />
    </div>
  );
};

export default Workspace;
